// item 1590/1591 (Linda: "Is there a way you can label the Ultrasonic Subscriptions like they
// used to be"; Chad: "we need to know the $ name easily and the friendly name next to each
// other … without having to click on anything") — a multi-unit customer's subscriptions share
// one name and one project, so six ledger rows read the same. What tells them apart is what the
// Stripe subscription carries on ITSELF: its description ("ID: CLUBSLASV1_$7") and its invoice
// footer ("… Friendly Name: ClubspaNV1 … Software ID: CLUBSLASV1_$7"), read by
// useStripeSubscriptionIdentities; a Basix-born unit can also say it in the schedule's own
// Invoice memo/footer note (item 1424). Pure and read-only: nothing new is stored anywhere, and
// a subscription that names no unit gets no tag, so every other row is untouched.
export interface SubscriptionUnitSource {
  // what the Stripe subscription carries on itself (item 1591)
  identity?: { description?: string | null; footer?: string | null } | null;
  // the schedule's own Basix notes (item 1424)
  headerNote?: string | null;
  footerNote?: string | null;
}

export interface SubscriptionUnit {
  softwareId?: string; // the "$ name": CLUBSLASV1_$7
  friendlyName?: string; // ClubspaNV1
  // "CLUBSLASV1_$7 · ClubspaNV1" — the $ name first, then the friendly name
  label?: string;
}

const FRIENDLY_NAME = /Friendly\s*Name\s*:\s*([^\r\n]+)/i;
const SOFTWARE_ID = /Software\s*ID\s*:\s*([^\r\n]+)/i;
// a Stripe description that is nothing but the id: "ID: CLUBSLASV1_$7"
const BARE_ID = /^\s*ID\s*:\s*([^\r\n]+)/i;

const firstMatch = (re: RegExp, texts: (string | null | undefined)[]): string | undefined => texts
  .map((t) => { const m = t ? re.exec(t) : null; return m ? m[1].trim() : ''; })
  .find((v) => v.length > 0);

export default function subscriptionUnit(src: SubscriptionUnitSource): SubscriptionUnit {
  const texts = [src.identity?.footer, src.identity?.description, src.headerNote, src.footerNote];
  const friendlyName = firstMatch(FRIENDLY_NAME, texts);
  const softwareId = firstMatch(SOFTWARE_ID, texts) ?? firstMatch(BARE_ID, [src.identity?.description]);
  const bits = [softwareId, friendlyName].filter((b): b is string => !!b);
  return { softwareId, friendlyName, label: bits.length > 0 ? bits.join(' · ') : undefined };
}

export const subscriptionUnitLabel = (src: SubscriptionUnitSource): string | undefined => subscriptionUnit(src).label;

// "Name (CLUBSLASV1_$7 · ClubspaNV1)" — the one spelling every ledger uses
export const withSubscriptionUnit = (name: string, src: SubscriptionUnitSource): string => {
  const label = subscriptionUnitLabel(src);
  return label ? `${name} (${label})` : name;
};
